/*
 * Copyright (C) 2020 to Present Applause App Quality, Inc. All rights reserved.
 */

import { CdkConnectedOverlay, ConnectedPosition, Overlay, ScrollStrategy } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { CollapsedPillsDirective } from './collapsed-pills.directive';
import { Subscription } from 'rxjs';

let nextDialogId = 0;

@Component({
    selector: 'ngf-pill-list',
    templateUrl: './pill-list.component.html',
    styleUrls: ['./pill-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class PillListComponent implements OnInit, OnDestroy {

  static readonly POSITION_TOP_PANEL_CLASS = 'overlay-position-top';
  static readonly POSITION_BOTTOM_PANEL_CLASS = 'overlay-position-bottom';

  readonly dialogId = `ngf-collapsed-pills-${++nextDialogId}`;

  @ContentChild(CollapsedPillsDirective)
  collapsedPills: CollapsedPillsDirective | null = null;

  @Input()
  showClearAll = false;

  @Input()
  positions: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -8,
      panelClass: PillListComponent.POSITION_TOP_PANEL_CLASS
    }
  ];
  elementRect: DOMRect;

  @Input()
  largePills: boolean;

  @Input()
  showChevron: boolean;

  @Output()
  openChanged = new EventEmitter<boolean>();

  @Output()
  clearAll = new EventEmitter<void>();

  @ViewChild('toggleBtn')
  toggleBtn: ElementRef;

  @ViewChild(CdkConnectedOverlay)
  overlayDir: CdkConnectedOverlay;

  chevronOffset: string | null = null;

  isOpen = false;
  isPositioned = false;

  scrollStrategy: ScrollStrategy;

  private collapsedPillsCountSub: Subscription | null = null;

  constructor(public elementRef: ElementRef,
              private cdr: ChangeDetectorRef,
              private overlay: Overlay) {
  }

  ngOnInit(): void {
    this.scrollStrategy = this.overlay.scrollStrategies.close();
  }

  toggle($event: MouseEvent): void {
    $event.stopPropagation();
    this.isOpen ? this.close() : this.open();
  }

  open(): void {
    if (!this.isOpen) {
      this.elementRect = this.elementRef.nativeElement.getBoundingClientRect();
      this.isPositioned = false;
      this.isOpen = true;
      this.cdr.markForCheck();

      /*
        When showing the collapsed pills list element, we want to ensure it gets properly closed
        when collapsedPills.count$ becomes zero
       */
      if (this.collapsedPillsCountSub === null && this.collapsedPills !== null) {
        this.collapsedPillsCountSub = this.collapsedPills.count$.subscribe((count) => {
          if (count === 0) {
            this.isOpen = false;
            this.cdr.markForCheck();
          }
        })
      }

    }

    this.openChanged.emit(this.isOpen);
  }

  close(): void {
    if (this.isOpen) {
      this.isOpen = false;
      this.chevronOffset = null;
      this.cdr.markForCheck();
      this.toggleBtn?.nativeElement?.focus({ preventScroll: true });
    }

    this.openChanged.emit(this.isOpen);
  }

  onToggleBtnEscape($event: KeyboardEvent): void {
    if (this.isOpen) {
      $event.stopPropagation();
      this.close();
    }
  }

  onOverlayEscape(event: KeyboardEvent): void {
    event.stopPropagation();
    this.close();
  }

  onPositionChange(): void {
    if (!this.overlayDir?.overlayRef || !this.toggleBtn?.nativeElement) {
      return;
    }

    const overlayRect = this.overlayDir.overlayRef.overlayElement.getBoundingClientRect();
    const btnRect = (this.toggleBtn.nativeElement as HTMLElement).getBoundingClientRect();
    const btnCenterX = btnRect.left + btnRect.width / 2;
    // This 16 and 8 are the chevron's width and half of the chevron's width.
    // This comes from the CSS so if the CSS changes, these numbers will need to update as well.
    const chevronLeft = Math.max(0, Math.min(overlayRect.width - 16, btnCenterX - overlayRect.left - 8));
    this.chevronOffset = `${chevronLeft}px`;
    this.isPositioned = true;
    this.cdr.markForCheck();
  }

  outsideClick($event: MouseEvent): void {
    if (this.toggleBtn?.nativeElement === $event.target) {
      return;
    }

    this.close();
  }

  onClearAll(): void {
    this.close();
    this.clearAll.emit();
  }

  ngOnDestroy(): void {
    if (this.collapsedPillsCountSub !== null) {
      this.collapsedPillsCountSub.unsubscribe();
    }
  }
}
